// Mock partner theme catalog. Mirrors the `theme` field shape the backend
// will eventually return from get-logo. Once the backend implements it,
// `getMockTheme` becomes obsolete — the response's `theme` object can be
// passed directly to `applyTheme`.
// Per-subdomain overrides. Add entries here once partners have their own
// palette decided. Anything not listed falls back to TEST_PALETTE during
// development so theming can be visually verified end-to-end.

// LA default palette — single source of truth for button/sidebar theming.
// CSS defaults in white-label-theme.css mirror these values.
export const DEFAULT_THEME = {
  primaryNavy: "#0d1830",
  primaryGreen: "#b2ea47",
  primaryGreen2:"#94C23C",
  hoverGreen: "rgb(148 194 60 / 98%)",
  darkBackground: "#19181c",
  lightGreenOverlay: "rgba(178, 234, 71, 0.102)",
  lightBgColor: "rgba(178, 234, 71, 0.102)",
  warningAccent: "#1FA89B",
  // Partner-catalog aliases (navy / green)
  primary: "#0d1830",
  secondary: "#b2ea47",
  secondaryGreen:"#94C23C",
  light:"#19181c",
};

const PARTNER_THEMES = {
  softude: {
    primary: "#22383c",
    secondary: "#1FA89B",
    lightBgColor: "#ffefd1",
    light:"#ffff",
    secondaryGreen:"#1FA89B"
  },
};

const TEST_PALETTE = {
  primary: "#22383c",
  secondary: "#1FA89B",
  lightBgColor: "#ffefd1",
  light:"#ffff",
  secondaryGreen:"#1FA89B"
};

export const getMockTheme = (subdomain) =>
  PARTNER_THEMES[subdomain] || TEST_PALETTE;

// Partner API themes often include only primary/secondary. Fill any missing
// keys from the subdomain mock palette so tokens like lightBgColor follow the
// same partner defaults as the sidebar colors.
export const mergePartnerTheme = (theme, subdomain) => ({
  ...getMockTheme(subdomain),
  ...(theme && typeof theme === "object" ? theme : {}),
});

// Writes a partner theme to :root as CSS custom properties so every
// `var(--la-*)` reference in stylesheets and inline styles re-resolves.
// Each field is optional — a missing key falls back to the default in
// white-label-theme.css.
//
// `primary` / `secondary` remain supported for partner catalogs and also
// update the navy/green named tokens plus the legacy aliases.
export const applyTheme = (theme) => {
  if (!theme || typeof theme !== "object") return;
  const root = document.documentElement;

  const navy = theme.primaryNavy || theme.primary;
  const green = theme.primaryGreen || theme.secondary;
  const lightBgColor = theme.lightBgColor ?? DEFAULT_THEME.lightBgColor;
  const light = theme.light || theme.light;
  const secondaryGreen = theme.secondaryGreen || theme.secondaryGreen;

  if (navy) {
    root.style.setProperty("--la-primary-navy", navy);
    root.style.setProperty("--la-primary", navy);
  }
  if (green) {
    root.style.setProperty("--la-primary-green", green);
    root.style.setProperty("--la-secondary", green);
  }
  if (theme.hoverGreen) {
    root.style.setProperty("--la-hover-green", theme.hoverGreen);
  }
  if (theme.darkBackground) {
    root.style.setProperty("--la-dark-background", theme.darkBackground);
  }
  if (theme.lightGreenOverlay) {
    root.style.setProperty("--la-light-green-overlay", theme.lightGreenOverlay);
  }
  if (theme.warningAccent) {
    root.style.setProperty("--la-warning-accent", theme.warningAccent);
  }
  if (lightBgColor) {
    root.style.setProperty("--la-light-bg-color", lightBgColor);
  }
   if (light) {
    root.style.setProperty("--la-light", light);
  }
  if(secondaryGreen){
     root.style.setProperty("--la-secondary-2", secondaryGreen);
  }
};
